import { ArrowRight, Code } from '@phosphor-icons/react';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Accordion from 'src/components/Accordion';
import { Button } from 'src/components/Button';
import { ContentWrapper } from 'src/components/ContentWrapper';
import { EquivalentCode } from 'src/components/EquivalentCode';
import { Heading } from 'src/components/Heading';
import { Input } from 'src/components/Input';
import { Label } from 'src/components/Label';
import { Loading } from 'src/components/Loading';
import { MultiSelectBox } from 'src/components/MultiSelectBox';
import { PromptTextArea } from 'src/components/PromptTextArea';
import { SelectBox } from 'src/components/SelectBox';
import { Tooltip } from 'src/components/Tooltip';
import { PROMPT_TAGS } from 'src/constants';
import { useNotification } from 'src/contexts/NotificationContext';
import { useRightHandSidebar } from 'src/contexts/RightHandSidebarContext';
import useAuthedRequest from 'src/hooks/useAuthedRequest';
import { Can, Subject } from 'src/hooks/usePermissions';
import { Prompt, PromptPayload } from 'src/interfaces/prompt.interface';
import { extractVariables } from 'src/utils/extractVariables';

type PromptProps = {
  editing?: boolean;
};

const PROMPT_TYPES = [
  { label: 'Prompt', value: 'prompt' },
  { label: 'Building Block', value: 'block' },
];

export const ManagePrompt = ({ editing = false }: PromptProps) => {
  const { get, patch, post } = useAuthedRequest();
  const { openSidebar, closeSidebar, isOpen } = useRightHandSidebar();

  const navigate = useNavigate();
  const { id: editId } = useParams();

  const notification = useNotification();

  const [dateSaved, setDateSaved] = useState<Date | null>(null);
  const [loading, setLoading] = useState(false);
  const [prompt, setPrompt] = useState<PromptPayload>({
    content: '',
    type: 'prompt',
  });
  const [promptBlocks, setPromptBlocks] = useState<Prompt[]>([]);

  const otherBlocks = promptBlocks.filter(
    (promptBlock) =>
      promptBlock.tags &&
      promptBlock.tags.length > 0 &&
      PROMPT_TAGS.filter((promptTag) => promptBlock.tags!.includes(promptTag))
        .length === 0,
  );
  const uncategorizedBlocks = promptBlocks.filter(
    (promptBlock) => !promptBlock.tags || !promptBlock.tags.length,
  );

  const handleSave = async () => {
    setLoading(true);

    if (editing) {
      await patch(`/prompts/${editId}`, {
        ...prompt,
        label: prompt.label || 'Untitled Prompt',
      });
      notification.success('Prompt updated successfully 🎉');
    } else {
      const createPrompt = await post('/prompts', {
        ...prompt,
        label: prompt.label || 'Untitled Prompt',
      });
      navigate(`/prompt/edit/${createPrompt.id}`);
      notification.success('Prompt created successfully 🎉');
    }

    setDateSaved(new Date());
    setLoading(false);
  };

  const endpoint = editing ? `prompts/update?id=${editId}` : 'prompts/create';

  const handlePromptEquivalentCode = () => {
    openSidebar(
      <EquivalentCode payload={prompt} endpoint={endpoint} method="POST" />,
      'Equivalent Code',
    );
  };

  const fetchPromptBlocks = async () => {
    const query = new URLSearchParams({
      size: '50',
      filters: JSON.stringify({
        type: 'block',
      }),
    });

    setLoading(true);

    const data = await get(`/prompts?${query.toString()}`);
    setPromptBlocks(data.items);

    setLoading(false);
  };

  useEffect(() => {
    fetchPromptBlocks();
  }, []);

  useEffect(() => {
    if (editing) {
      const fetchPrompt = async () => {
        setLoading(true);
        const prompt = await get(`/prompts/${editId}`);
        setPrompt(prompt);
        setLoading(false);
      };

      fetchPrompt();
    }
  }, [editing, editId]);

  useEffect(() => {
    if (isOpen && !loading) {
      handlePromptEquivalentCode();
    }
  }, [prompt, isOpen]);

  return (
    <div className="flex-1">
      <Heading
        title={
          editing ? `Editing Prompt: ${prompt.label || editId}` : 'New Prompt'
        }
      />

      {loading ? (
        <div className="flex justify-center items-center h-full">
          <Loading size="8" className="mt-8" />
        </div>
      ) : (
        <ContentWrapper>
          <div className="grid grid-cols-1 md:grid-cols-5">
            {promptBlocks.length > 0 && (
              <div className="max-sm:hidden md:col-span-1 text-neutral-700 mr-4">
                <div className="font-medium text-lg text-primary transition-all duration-200 focus:outline-none py-2 px-1">
                  Building Blocks
                </div>
                <div className="w-full bg-neutral-0">
                  {PROMPT_TAGS.map((promptTag) => {
                    const tagBlocks = promptBlocks.filter((promptBlock) =>
                      promptBlock.tags?.includes(promptTag),
                    );
                    if (!tagBlocks || !tagBlocks.length) {
                      return;
                    }
                    return (
                      <Accordion
                        key={promptTag}
                        title={`${promptTag.charAt(0).toUpperCase()}${promptTag.slice(1)}s`}
                      >
                        {tagBlocks.map((tagBlock) => {
                          return (
                            <Tooltip
                              className="w-full"
                              content={tagBlock.content}
                            >
                              <button
                                className="inline-flex justify-between w-full text-sm font-medium hover:bg-neutral-50 hover:text-primary-600 focus:bg-neutral-50 focus:text-primary-600"
                                onClick={() =>
                                  setPrompt({
                                    ...prompt,
                                    content: prompt.content + tagBlock.content,
                                  })
                                }
                              >
                                {tagBlock.label}
                                <ArrowRight size={16} />
                              </button>
                            </Tooltip>
                          );
                        })}
                      </Accordion>
                    );
                  })}
                  {otherBlocks.length > 0 && (
                    <Accordion key={'other'} title={`Other`}>
                      {otherBlocks.map((otherBlock) => {
                        return (
                          <Tooltip
                            className="w-full"
                            content={otherBlock.content}
                          >
                            <button
                              className="inline-flex justify-between w-full text-sm font-medium hover:bg-neutral-50 hover:text-primary-600 focus:z-10 focus:ring-2 focus:ring-primary-600 focus:text-primary-600"
                              onClick={() =>
                                setPrompt({
                                  ...prompt,
                                  content: prompt.content + otherBlock.content,
                                })
                              }
                            >
                              {otherBlock.label}
                              <ArrowRight size={16} />
                            </button>
                          </Tooltip>
                        );
                      })}
                    </Accordion>
                  )}
                  {uncategorizedBlocks.length > 0 && (
                    <Accordion
                      key={'uncategorized'}
                      title={`Uncategorized`}
                      className="text-"
                    >
                      {uncategorizedBlocks.map((uncategorizedBlock) => {
                        return (
                          <Tooltip
                            className="w-full"
                            content={uncategorizedBlock.content}
                          >
                            <button
                              className="inline-flex justify-between w-full text-sm font-medium hover:bg-neutral-50 hover:text-primary-600 focus:z-10 focus:ring-2 focus:ring-primary-600 focus:text-primary-600"
                              onClick={() =>
                                setPrompt({
                                  ...prompt,
                                  content:
                                    prompt.content + uncategorizedBlock.content,
                                })
                              }
                            >
                              {uncategorizedBlock.label}
                              <ArrowRight size={16} />
                            </button>
                          </Tooltip>
                        );
                      })}
                    </Accordion>
                  )}
                </div>
              </div>
            )}
            <div
              className={`${promptBlocks.length > 0 ? 'col-span-3' : 'col-span-4'} mr-4`}
            >
              <div className="w-full md:col-span-2 lg:col-span-2 text-sm mt-2">
                <div className="flex">
                  <div className="w-2/3">
                    <Label htmlFor="prompt-name">Name</Label>
                    <Input
                      type="text"
                      id="label"
                      placeholder="Name"
                      value={prompt.label}
                      onChange={(e) =>
                        setPrompt({ ...prompt, label: e.target.value })
                      }
                      disabled={loading || (!prompt.user_id && !!editId)}
                    />
                  </div>
                  <div className="ml-2 w-1/3">
                    <Label htmlFor="prompt-types">Type</Label>
                    <SelectBox
                      key="prompt-types"
                      options={PROMPT_TYPES}
                      defaultValue={PROMPT_TYPES.find(
                        (promptType) => promptType.value === prompt.type,
                      )}
                      variant="outlined"
                      color="primary"
                      size="medium"
                      onChange={(value) => {
                        setPrompt({ ...prompt, type: value });
                      }}
                      className="col-span-2"
                      disabled={loading || (!prompt.user_id && !!editId)}
                    />
                  </div>
                </div>
                {prompt.type === 'block' && (
                  <div className="mt-2">
                    <Label htmlFor="prompt-tags-selector">Tags</Label>
                    <div className="w-full">
                      <MultiSelectBox
                        key="prompt-tags-selector"
                        defaultValue={(prompt.tags || []).map((tag) => ({
                          value: tag,
                          label: tag,
                        }))}
                        options={PROMPT_TAGS.map((tag) => ({
                          value: tag,
                          label: tag,
                        }))}
                        variant="outlined"
                        color="primary"
                        size="medium"
                        className="w-full"
                        onChange={(value) =>
                          setPrompt({ ...prompt, tags: value })
                        }
                        disabled={loading || (!prompt.user_id && !!editId)}
                      />
                    </div>
                  </div>
                )}
              </div>

              <Label className="text-sm mt-2" htmlFor="prompt-content">
                Content
              </Label>
              <PromptTextArea
                value={prompt.content}
                onChange={(e) =>
                  setPrompt({ ...prompt, content: e.target.value })
                }
                loading={loading}
                variables={extractVariables(prompt.content)}
                placeholder="Prompt Content"
                rows={11}
              />

              <div className="flex items-center mt-4 justify-end">
                {dateSaved && !loading && (
                  <p className="mr-4 text-success">
                    Last saved at {dateSaved.toLocaleTimeString()}
                  </p>
                )}

                <Button
                  className="mr-4"
                  onClick={() => navigate('/prompts')}
                  disabled={loading}
                  variant="outlined"
                >
                  Cancel
                </Button>

                <Can operation="update" subject={Subject.PROMPTS}>
                  <Button
                    onClick={handleSave}
                    disabled={
                      loading ||
                      !prompt.content ||
                      (!prompt.user_id && !!editId)
                    }
                  >
                    {loading ? 'Saving...' : 'Save'}
                  </Button>
                </Can>
              </div>
            </div>
            <div className="col-span-1">
              <Button
                onClick={() =>
                  isOpen ? closeSidebar() : handlePromptEquivalentCode()
                }
                className="mt-2 w-full md:col-span-1 lg:col-span-1 flex items-center justify-center"
              >
                <Code className="mr-2" size={20} />
                Show equivalent code
              </Button>
              <div className="mt-4">
                <div>
                  <div className="bg-neutral-50 p-4 rounded-md">
                    <h3>Prompt Guidance</h3>
                    <div className="text-neutral-500 text-sm mt-2">
                      Here are some tips for creating a great prompt:
                      <ul className="list-disc ml-5 mt-2">
                        <li>
                          Avoid overly complex instructions. Simple instructions
                          are more reliable
                        </li>
                        <li>
                          If using uncommon words or proper nouns, write it out
                          phonetically
                        </li>
                        <li>
                          When working with large prompts, split it into
                          sections
                        </li>
                        <li>Provide some examples of desired behavior</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </ContentWrapper>
      )}
    </div>
  );
};
